import { TIER_FEATURES } from "./subscriptionFeatures";

/**
 * SUB-005 — single source of truth for subscription CARD bullets.
 *
 * The server already ships an admin-editable, per-package feature list on each
 * subscription package as `feature_splits` (Backend `FeatureSplit` model →
 * `FeatureSplitSerializer`: `{ id, text, is_highlighted, tooltip }`). The cards
 * historically ignored it and rendered a hardcoded `TIER_FEATURES` constant
 * instead, so the two drifted (SUB-005). This resolver makes the SERVER the
 * source of truth: when a package carries `feature_splits`, the card renders
 * those; the hardcoded `TIER_FEATURES` remains ONLY as a safety fallback for
 * packages the server sends without any splits (so a card never renders blank).
 *
 * Once the dev confirms `FeatureSplit` rows are populated & correct for every
 * live tier (see the SUB-002/SUB-004 data-dump ask), the fallback and
 * `subscriptionFeatures.js` can be deleted outright.
 */

/**
 * Map one server `FeatureSplit` onto the shape the card renderer expects
 * (`{ feature_description, tooltip }`). `tooltip` is a string when present,
 * else `false` (the renderer only shows the info icon for string tooltips).
 * `text` is admin-authored rich text and is rendered via the same
 * `dangerouslySetInnerHTML` path the hardcoded descriptions already used —
 * same trust level (admin, not end-user), no new sink introduced.
 */
export function mapFeatureSplit(split) {
  return {
    feature_description: split && typeof split.text === "string" ? split.text : "",
    tooltip: split && split.tooltip ? split.tooltip : false,
    is_highlighted: !split || split.is_highlighted !== false,
  };
}

/**
 * Resolve the bullet list a card should render.
 *
 * @param {Object} args
 * @param {Array|undefined} args.featureSplits - server `item.feature_splits`.
 * @param {number} args.subTier - the package `sub_tier` (0..3).
 * @returns {Array<{feature_description: string, tooltip: (string|false)}>}
 */
export function resolveCardFeatures({ featureSplits, subTier, cardQuotas } = {}) {
  if (Array.isArray(featureSplits)) {
    const usable = featureSplits
      .filter((s) => s && typeof s.text === "string" && s.text.trim() !== "")
      .map(mapFeatureSplit);
    if (usable.length > 0) return usable;
  }
  const tier = TIER_FEATURES.find((t) => t.sub_tier === subTier);
  return tier ? applyCardQuotas(tier.features, cardQuotas) : [];
}

/**
 * T22.454 — the hardcoded fallback drifted from the server limits: on staging
 * (a Production clone, no `feature_splits`) the Essential card said "500MB" +
 * "10 AI Wheel Tokens" while Compare Plans and the enforcement read
 * SubscriptionFeature: Unlimited + 40 (ProSuite card 8GB vs Unlimited). The
 * package list now ships `card_quotas` — the same `value_display` the Compare
 * table renders — and those values replace the fallback's storage / token
 * numbers. A bullet whose number already agrees keeps its original copy.
 */
const isUnlimitedDisplay = (v) => /^(unlimited|inf(inite)?|∞)$/i.test(v.trim());
const normSize = (v) => v.replace(/<[^>]*>/g, "").replace(/\s+/g, "").replace(/s$/i, "").toLowerCase();

const STORAGE_RE = /^<b>([^<]*)<\/b> of Storage/;
const TOKENS_RE = /^(\d+) (AI Wheel Tokens.*)$/;

export function applyCardQuotas(features, cardQuotas) {
  if (!cardQuotas || typeof cardQuotas !== "object") return features;
  const space = typeof cardQuotas.space_allocated === "string" ? cardQuotas.space_allocated.trim() : "";
  const tokens = typeof cardQuotas.ai_wheel_tokens === "string" ? cardQuotas.ai_wheel_tokens.trim() : "";
  if (!space && !tokens) return features;
  return features.map((f) => {
    const desc = f.feature_description;
    const storage = space && STORAGE_RE.exec(desc);
    if (storage && normSize(storage[1]) !== normSize(space)) {
      return {
        ...f,
        feature_description: isUnlimitedDisplay(space)
          ? "<b>Unlimited</b> Storage"
          : `<b>${space}</b> of Storage`,
      };
    }
    const tok = tokens && TOKENS_RE.exec(desc);
    if (tok && tok[1] !== tokens) {
      return {
        ...f,
        feature_description: `${isUnlimitedDisplay(tokens) ? "Unlimited" : tokens} ${tok[2]}`,
      };
    }
    return f;
  });
}
